import { useEffect, useRef, memo } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { type FunctionDeclaration, SchemaType } from "@google/generative-ai";

interface AltairProps {
  collectDetails: (note: string) => void;
  getGrades: (studentId: string) => void;
}

const declarations: FunctionDeclaration[] = [{
  name: "collectDetails",
  parameters: {
    type: SchemaType.OBJECT,
    properties: {
      text: {
        type: SchemaType.STRING,
        description: "Save students details as they provide.",
      },
    },
    required: ["text"],
  },
  description: "Keep track of details collected from students",
},
{
  name: "getGrades",
  parameters: {
    type: SchemaType.OBJECT,
    properties: {
      studentId: {
        type: SchemaType.STRING,
        description: "Get grades for a student",
      },
    },
    required: ["studentId"],
  },
  description: "Get grades for a student",
}];

function AltairComponent({ collectDetails, getGrades }: AltairProps) {
  const { client, setConfig } = useLiveAPIContext();

  useEffect(() => {
    setConfig({
      model: "models/gemini-2.0-flash-exp",
      generationConfig: {
        responseModalities: "audio",
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: "Kore" } },
        },
      },
      systemInstruction: {
        parts: [
          {
            text: 'You are an admin staff from GIIS. You help students and parents in admission process and information. Help them with their queries related to the institution and the admission process. Collect as much information as possible from students and parents and save details using function "collectDetails".',
          },
        ],
      },
      tools: [
        { functionDeclarations: declarations },
      ],
    });
  }, [setConfig]);

  useEffect(() => {
    if (!client) return;

    const onToolCall = async (event: any) => {
      const functionCall = event.functionCalls[0];
      if (!functionCall) return;

      switch (functionCall.name) {
        case "collectDetails":
          const text = functionCall.args?.text;
          if (text) await collectDetails(text);
          break;
        case "getGrades":
          const args = functionCall.args;
          if (args) {
            return await getGrades(args.studentId || "");
          }
          break;
      }
    };

    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client, collectDetails, getGrades]);

  const embedRef = useRef<HTMLDivElement>(null);

  return <div className="vega-embed" ref={embedRef} />;
}

export const Altair = memo(AltairComponent);
